import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { settingsApi, emailApi } from '../../utils/api';

const GmailSettings = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    gmailClientId: '',
    gmailClientSecret: '',
  });

  const { data: settings, isLoading } = useQuery({
    queryKey: ['gmail-settings'],
    queryFn: settingsApi.getGmailSettings
  });

  const updateMutation = useMutation({
    mutationFn: settingsApi.updateGmailSettings,
    onSuccess: () => {
      setIsEditing(false);
    }
  });

  const getAuthUrlMutation = useMutation({
    mutationFn: settingsApi.getGmailAuthUrl,
    onSuccess: (data) => {
     
      window.location.href = data.data.url;
    }
  });

  const syncEmailsMutation = useMutation({
    mutationFn: emailApi.syncEmails,
    onSuccess: (data) => {
      
      alert(`${data.data.processed} emails synchronisés avec succès`);
    },
    onError: (error) => {
      alert('Erreur lors de la synchronisation des emails');
      console.error('Sync error:', error);
    }
  });

  const handleEdit = () => {
    setFormData({
      gmailClientId: settings?.data.gmailClientId || '',
      gmailClientSecret: settings?.data.gmailClientSecret || '',
    });
    setIsEditing(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    updateMutation.mutate(formData);
  };

  const handleGmailAuth = () => {
    getAuthUrlMutation.mutate();
  };

  const handleSync = () => {
    syncEmailsMutation.mutate();
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('gmailConnected') === 'true') {
      alert('Gmail connecté avec succès !');
    } else if (params.get('gmailError') === 'true') {
      alert('Erreur lors de la connexion Gmail');
    }
  }, []);

  if (isLoading) {
    return (
      <div className="animate-pulse p-4">
        <div className="h-4 bg-gray-200 rounded w-1/4"></div>
        <div className="space-y-3 mt-4">
          <div className="h-4 bg-gray-200 rounded"></div>
          <div className="h-4 bg-gray-200 rounded"></div>
          <div className="h-4 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-medium text-gray-900">Configuration Gmail</h2>
        <div className="space-x-3">
          {!isEditing && (
            <>
              <button
                onClick={handleEdit}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                Modifier
              </button>
              {settings?.data.gmailClientId && settings?.data.gmailClientSecret && !settings?.data.gmailRefreshToken && (
                <button
                  onClick={handleGmailAuth}
                  disabled={getAuthUrlMutation.isPending}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                >
                  {getAuthUrlMutation.isPending ? 'Connexion...' : 'Se connecter à Gmail'}
                </button>
              )}
              {settings?.data.gmailRefreshToken && (
                <button
                  onClick={handleSync}
                  disabled={syncEmailsMutation.isPending}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50"
                >
                  {syncEmailsMutation.isPending ? 'Synchronisation...' : 'Synchroniser les emails'}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {syncEmailsMutation.isSuccess && (
        <div className="mb-4 p-4 bg-green-50 text-green-700 rounded-md">
          {`${syncEmailsMutation.data.data.processed} emails synchronisés avec succès`}
        </div>
      )}

      {syncEmailsMutation.isError && (
        <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-md">
          Erreur lors de la synchronisation des emails
        </div>
      )}

      {isEditing ? (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="gmailClientId" className="block text-sm font-medium text-gray-700">
              Client ID
            </label>
            <input
              type="text"
              id="gmailClientId"
              value={formData.gmailClientId}
              onChange={(e) => setFormData({ ...formData, gmailClientId: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="gmailClientSecret" className="block text-sm font-medium text-gray-700">
              Client Secret
            </label>
            <input
              type="password"
              id="gmailClientSecret"
              value={formData.gmailClientSecret}
              onChange={(e) => setFormData({ ...formData, gmailClientSecret: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => setIsEditing(false)}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={updateMutation.isPending}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50"
            >
              {updateMutation.isPending ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Client ID</label>
            <div className="mt-1 text-sm text-gray-900">
              {settings?.data.gmailClientId ? '••••••••' + settings.data.gmailClientId.slice(-4) : 'Non configuré'}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Client Secret</label>
            <div className="mt-1 text-sm text-gray-900">
              {settings?.data.gmailClientSecret ? '••••••••' : 'Non configuré'}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">État de la connexion</label>
            <div className="mt-1 text-sm text-gray-900">
              {settings?.data.gmailRefreshToken ? (
                <span className="text-green-600">Connecté à Gmail</span>
              ) : (
                <span className="text-yellow-600">Non connecté</span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GmailSettings;
